import React, { useState, useCallback } from 'react';
import { useSearchParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { authConfirmPassword } from '@api/auth';
import AuthLayout from '@components/1-atoms/Layouts/AuthLayout/AuthLayout';
import {
  Title,
  Subtitle,
  Form,
  Input,
  SubmitButton,
  ErrorText,
  ErrorBox,
  LinkText,
} from '../auth.styled';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const codeFromUrl = searchParams.get('code') || '';
  const emailFromUrl = searchParams.get('email') || '';
  const emailFromState = (location.state as { email?: string })?.email || '';
  const initialEmail = emailFromUrl || emailFromState;

  const schema = Yup.object({
    email: Yup.string().email('Invalid email').required('Email is required'),
    code: Yup.string().required('Reset code is required'),
    newPassword: Yup.string().min(8, 'Must be at least 8 characters').required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Passwords must match')
      .required('Please confirm your password'),
  });

  const handleSubmit = useCallback(async (values: {
    email: string; code: string; newPassword: string; confirmPassword: string;
  }) => {
    setLoading(true);
    setError('');
    try {
      await authConfirmPassword(values.email, values.code, values.newPassword);
      navigate('/auth/login', { state: { passwordReset: true } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Password reset failed');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  return (
    <AuthLayout>
      <Title>Reset password</Title>
      <Subtitle>Enter the code we sent to your email and set a new password.</Subtitle>
      <Formik
        initialValues={{ email: initialEmail, code: codeFromUrl, newPassword: '', confirmPassword: '' }}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit: formSubmit }) => (
          <Form onSubmit={formSubmit}>
            {error && <ErrorBox>{error}</ErrorBox>}
            <Input name="email" type="email" placeholder="Email address"
              value={values.email} onChange={handleChange} onBlur={handleBlur}
              $error={!!(touched.email && errors.email)} />
            <Input name="code" type="text" placeholder="Reset code"
              value={values.code} onChange={handleChange} onBlur={handleBlur}
              $error={!!(touched.code && errors.code)} autoFocus={!!codeFromUrl} />
            <Input name="newPassword" type="password" placeholder="New password"
              value={values.newPassword} onChange={handleChange} onBlur={handleBlur}
              $error={!!(touched.newPassword && errors.newPassword)} />
            {touched.newPassword && errors.newPassword && <ErrorText>{errors.newPassword}</ErrorText>}
            <Input name="confirmPassword" type="password" placeholder="Confirm password"
              value={values.confirmPassword} onChange={handleChange} onBlur={handleBlur}
              $error={!!(touched.confirmPassword && errors.confirmPassword)} />
            {touched.confirmPassword && errors.confirmPassword && <ErrorText>{errors.confirmPassword}</ErrorText>}
            <SubmitButton type="submit" disabled={loading} $loading={loading}>
              {loading ? 'Resetting...' : 'Reset password'}
            </SubmitButton>
            <LinkText><Link to="/auth/login">Back to login</Link></LinkText>
          </Form>
        )}
      </Formik>
    </AuthLayout>
  );
};

export default ResetPassword;
